import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, where, addDoc, deleteDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import emailjs from '@emailjs/browser'; // 🔥 IMPORTAMOS EMAILJS
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt, FaUserPlus, FaWhatsapp, FaClock, FaEdit, FaTrashAlt, FaLock, FaListUl, FaChevronRight, FaBell, FaTimes, FaCalendarCheck, FaEnvelope } from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

registerLocale('es', es);

// 🔥 JARVIS: Matriz de horarios agrupada en pares exactos (2 botones por bloque) para coincidir con la vista pública
const BLOQUES_HORARIOS = [
    { id: "Bloque 1 (10:00 - 11:00)", opciones: ["10:00", "10:30"] },
    { id: "Bloque 2 (11:00 - 12:00)", opciones: ["11:00", "11:30"] },
    { id: "Bloque 3 (12:00 - 13:00)", opciones: ["12:00", "12:30"] },
    { id: "Bloque 4 (15:00 - 16:00)", opciones: ["15:00", "15:30"] },
    { id: "Bloque 5 (16:00 - 17:00)", opciones: ["16:00", "16:30"] },
    { id: "Bloque 6 (17:00 - 18:00)", opciones: ["17:00", "17:30"] }
];

const CalendarioView = () => {
    const [fecha, setFecha] = useState(new Date());
    const [horasOcupadasRaw, setHorasOcupadasRaw] = useState([]);
    const [citasDetalle, setCitasDetalle] = useState([]);
    const [todasLasCitas, setTodasLasCitas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [horaSeleccionada, setHoraSeleccionada] = useState(null);
    const [form, setForm] = useState({ nombre: '', email: '', telefono: '', notas: '' });

    // 🔥 Estados para libertad total de horarios
    const [usarHoraLibre, setUsarHoraLibre] = useState(false);
    const [horaManual, setHoraManual] = useState("09");
    const [minutoManual, setMinutoManual] = useState("00");

    // 🔥 JARVIS: Estados para el sistema de alertas y modal
    const [alertasEmitidas, setAlertasEmitidas] = useState([]);
    const [citaSeleccionada, setCitaSeleccionada] = useState(null); // Estado para abrir la tarjeta de detalle
    const audioRef = useRef(null);

    // 🔓 Libertad total: cualquier día es agendable internamente
    const esDiaLaboral = (date) => {
        return true; 
    };

    // 1. Efecto para traer las citas del día seleccionado (Vista Calendario)
    useEffect(() => {
        const inicio = new Date(fecha);
        inicio.setHours(0, 0, 0, 0);
        const fin = new Date(fecha);
        fin.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, 'citas'),
            where('fechaInicio', '>=', Timestamp.fromDate(inicio)),
            where('fechaInicio', '<=', Timestamp.fromDate(fin))
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const horas = [];
            const detalles = [];
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                const d = data.fechaInicio.toDate();
                const HHmm = d.getHours().toString().padStart(2, '0') + ":" + 
                             d.getMinutes().toString().padStart(2, '0');
                horas.push(HHmm);
                detalles.push({ id: doc.id, ...data, horaFmt: HHmm });
            });
            setHorasOcupadasRaw(horas);
            setCitasDetalle(detalles);
        });
        return () => unsubscribe();
    }, [fecha]);

    // 2. 🔥 Efecto para la "Sección Interna": Traer todas las citas futuras (Visibilidad Total)
    useEffect(() => {
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        const qGlobal = query(
            collection(db, 'citas'),
            where('fechaInicio', '>=', Timestamp.fromDate(hoy)),
            orderBy('fechaInicio', 'asc')
        );

        const unsubscribeGlobal = onSnapshot(qGlobal, (snapshot) => {
            const listaGlobal = snapshot.docs.map(doc => {
                const data = doc.data();
                const d = data.fechaInicio.toDate();
                return {
                    id: doc.id,
                    ...data,
                    fechaFmt: d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                    horaFmt: d.getHours().toString().padStart(2, '0') + ":" + d.getMinutes().toString().padStart(2, '0'),
                    fechaObjeto: d
                };
            });
            setTodasLasCitas(listaGlobal);
        });
        return () => unsubscribeGlobal();
    }, []);

    // 3. 🚨 JARVIS: SISTEMA DE VIGILANCIA Y ALERTAS (Solo para citas a punto de iniciar)
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio('/alerta.mp3');
        }

        const intervalId = setInterval(() => {
            const ahora = new Date();
            
            todasLasCitas.forEach(cita => {
                if (cita.estado === 'Bloqueado') return;
                
                const diferenciaMs = cita.fechaObjeto.getTime() - ahora.getTime();
                const diferenciaMinutos = Math.floor(diferenciaMs / 60000);

                if (diferenciaMinutos > 0 && diferenciaMinutos <= 30 && !alertasEmitidas.includes(cita.id)) {
                    audioRef.current.play().catch(e => console.log("Audio autoplay blocked"));

                    toast((t) => (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 font-black text-orange-600 text-lg border-b pb-2">
                                <FaBell className="animate-bounce" /> ¡CITA EN {diferenciaMinutos} MINUTOS!
                            </div>
                            <span className="font-bold text-slate-800">{cita.nombreCliente}</span>
                            <span className="text-sm text-slate-500">Hora: {cita.horaFmt} hs</span>
                            {cita.telefonoCliente && (
                                <a href={`https://wa.me/${cita.telefonoCliente}`} target="_blank" rel="noreferrer" className="mt-2 text-xs bg-green-500 text-white p-2 rounded-lg text-center font-bold hover:bg-green-600 transition-colors">
                                    CONTACTAR CLIENTE
                                </a>
                            )}
                        </div>
                    ), { 
                        duration: 15000, 
                        icon: '🚨',
                        style: { border: '2px solid #ea580c', padding: '16px', borderRadius: '16px' }
                    });

                    setAlertasEmitidas(prev => [...prev, cita.id]);
                }
            });
        }, 30000);

        return () => clearInterval(intervalId);
    }, [todasLasCitas, alertasEmitidas]);

    // 🔥 JARVIS: Motor de EmailJS activado
    const enviarConfirmacionEmail = async (datosCita) => {
        const templateParams = {
            nombreCliente: datosCita.nombre,
            emailCliente: datosCita.email,
            fecha: datosCita.fechaStr,
            hora: datosCita.hora,
            notas: datosCita.notas || "Agendado internamente por el equipo."
        };

        try {
            await emailjs.send(
                'service_ywv3c8t',    
                'template_8to87xj',   
                templateParams,
                'sfiGikjVk7t3NudEy'   
            );
            console.log("GRID: Email interno enviado satisfactoriamente");
        } catch (error) {
            console.error("Error al enviar el correo interno:", error);
            throw error; 
        }
    };

    const handleAgendar = async (e) => {
        e.preventDefault();
        
        let horaFinal = horaSeleccionada;
        if (usarHoraLibre) {
            horaFinal = `${horaManual.padStart(2, '0')}:${minutoManual.padStart(2, '0')}`;
        }

        if (!horaFinal) return toast.error("Selecciona o ingresa una hora.");
        
        setLoading(true);
        try {
            const [h, m] = horaFinal.split(':');
            const f = new Date(fecha);
            f.setHours(parseInt(h), parseInt(m), 0);
            
            await addDoc(collection(db, 'citas'), {
                nombreCliente: form.nombre,
                emailCliente: form.email,
                telefonoCliente: form.telefono,
                notas: form.notas,
                fechaInicio: Timestamp.fromDate(f),
                origen: 'Agendado Interno',
                creadoEn: Timestamp.now(),
                estado: 'Confirmada'
            });

            // Disparar correo si hay email
            if (form.email && form.email.trim() !== '') {
                try {
                    await enviarConfirmacionEmail({
                        nombre: form.nombre,
                        email: form.email,
                        fechaStr: fecha.toLocaleDateString('es-ES'),
                        hora: horaFinal,
                        notas: form.notas
                    });
                    toast.success("Evento guardado y correo enviado al cliente.");
                } catch (emailError) {
                    toast.error("Evento guardado, pero el correo falló.");
                }
            } else {
                toast.success("Evento guardado en Hoppy Calendar");
            }

            setForm({ nombre: '', email: '', telefono: '', notas: '' });
            setHoraSeleccionada(null);
            setUsarHoraLibre(false);
        } catch (e) { 
            toast.error("Error al agendar"); 
        }
        setLoading(false);
    };

    const handleBloquear = async () => {
        let horaFinal = horaSeleccionada;
        if (usarHoraLibre) {
            horaFinal = `${horaManual.padStart(2, '0')}:${minutoManual.padStart(2, '0')}`;
        }

        if (!horaFinal) return toast.error("Selecciona una hora para bloquear.");
        
        setLoading(true);
        try {
            const [h, m] = horaFinal.split(':');
            const f = new Date(fecha);
            f.setHours(parseInt(h), parseInt(m), 0);
            await addDoc(collection(db, 'citas'), {
                nombreCliente: "BLOQUEO DE SEGURIDAD",
                notas: "Bloqueo manual desde panel Hoppytime",
                fechaInicio: Timestamp.fromDate(f),
                origen: 'Bloqueo Manual',
                creadoEn: Timestamp.now(),
                estado: 'Bloqueado'
            });
            toast.success("Bloque horario cerrado");
            setHoraSeleccionada(null);
            setUsarHoraLibre(false);
        } catch (e) { toast.error("Error al bloquear"); }
        setLoading(false);
    };

    const handleEliminarCita = async (id, e) => {
        if (e) e.stopPropagation();
        if (window.confirm(`¿Estás seguro de que quieres eliminar esto? Se liberará el bloque horario.`)) {
            try {
                await deleteDoc(doc(db, 'citas', id));
                toast.success("Espacio liberado");
                if (citaSeleccionada && citaSeleccionada.id === id) {
                    setCitaSeleccionada(null);
                }
            } catch (error) {
                toast.error("Error al procesar");
            }
        }
    };

    return (
        <div className="p-4 lg:p-8 bg-slate-50 min-h-screen relative">
            <Toaster position="top-right" />
            
            <div className="max-w-7xl mx-auto space-y-8">
                
                {/* SECCIÓN 1: CALENDARIO Y REGISTRO */}
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* IZQUIERDA: SELECTORES */}
                    <div className="lg:w-1/3 space-y-6">
                        <div className="bg-[#18BC9C] p-6 rounded-[2rem] text-white shadow-xl">
                            <h3 className="font-bold flex items-center gap-2 mb-4 text-lg"><FaCalendarAlt /> Hoppy Calendar Interno</h3>
                            <DatePicker 
                                selected={fecha} 
                                onChange={d => { setFecha(d); setHoraSeleccionada(null); }} 
                                inline locale="es" 
                            />
                        </div>

                        <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-200">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Disponibilidad</h3>
                                <button 
                                    onClick={() => { setUsarHoraLibre(!usarHoraLibre); setHoraSeleccionada(null); }}
                                    className={`text-[10px] font-bold px-2 py-1 rounded-lg border transition-all ${usarHoraLibre ? 'bg-orange-500 text-white border-orange-500' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                                >
                                    {usarHoraLibre ? 'VER BLOQUES' : 'MODO LIBRE'}
                                </button>
                            </div>

                            {usarHoraLibre ? (
                                <div className="p-4 bg-orange-50 rounded-2xl border-2 border-dashed border-orange-200">
                                    <p className="text-[10px] font-black text-orange-600 mb-3 uppercase text-center italic">Cualquier hora es válida</p>
                                    <div className="flex items-center justify-center gap-2">
                                        <select value={horaManual} onChange={e => setHoraManual(e.target.value)} className="p-3 text-lg rounded-xl bg-white font-black text-slate-700 border border-orange-200 outline-none shadow-sm">
                                            {Array.from({length: 24}, (_, i) => i.toString().padStart(2, '0')).map(h => <option key={h} value={h}>{h} hs</option>)}
                                        </select>
                                        <span className="font-bold text-orange-400 text-xl">:</span>
                                        <select value={minutoManual} onChange={e => setMinutoManual(e.target.value)} className="p-3 text-lg rounded-xl bg-white font-black text-slate-700 border border-orange-200 outline-none shadow-sm">
                                            {["00", "15", "30", "45"].map(m => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {BLOQUES_HORARIOS.map(b => (
                                        <div key={b.id} className="p-3 bg-slate-50 rounded-2xl">
                                            <p className="text-[10px] font-bold text-slate-400 mb-2 uppercase">{b.id}</p>
                                            <div className="grid grid-cols-2 gap-2">
                                                {b.opciones.map(h => {
                                                    const ocupada = horasOcupadasRaw.includes(h);
                                                    const esBloqueo = citasDetalle.find(c => c.horaFmt === h && c.estado === 'Bloqueado');
                                                    
                                                    return (
                                                        <button key={h} disabled={ocupada} onClick={() => setHoraSeleccionada(h)}
                                                            className={`py-4 rounded-xl text-lg font-black border-2 transition-all ${
                                                                esBloqueo ? 'bg-slate-800 text-white border-slate-800' :
                                                                ocupada ? 'bg-red-500 text-white border-red-500 shadow-inner' :
                                                                horaSeleccionada === h ? 'bg-[#18BC9C] text-white border-[#18BC9C] scale-105 shadow-md' : 
                                                                'bg-white text-[#18BC9C] border-teal-50 hover:border-[#18BC9C]'
                                                            }`}>
                                                            {h} {esBloqueo ? '🔒' : ocupada ? '✓' : ''}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* DERECHA: FORMULARIO Y CITAS DEL DÍA */}
                    <div className="lg:w-2/3 space-y-6">
                        <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-slate-100">
                            <h3 className="text-xl font-black text-slate-800 mb-6 flex items-center gap-2">
                                <FaUserPlus className="text-[#18BC9C]" /> Agendar en Hoppytime { (horaSeleccionada || usarHoraLibre) && <span className="text-[#18BC9C]">({usarHoraLibre ? `${horaManual}:${minutoManual}` : horaSeleccionada} hs)</span>}
                            </h3>
                            <form onSubmit={handleAgendar} className="grid grid-cols-1 gap-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input required placeholder="Actividad / Instalación / Cliente" className="p-4 bg-slate-50 rounded-2xl outline-none focus:ring-2 focus:ring-[#18BC9C]" value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} />
                                    <input required placeholder="Teléfono" className="p-4 bg-slate-50 rounded-2xl outline-none focus:ring-2 focus:ring-[#18BC9C]" value={form.telefono} onChange={e => setForm({...form, telefono: e.target.value})} />
                                    {/* 🔥 JARVIS: Texto cambiado de Email Hoppy a Email */}
                                    <input placeholder="Email" className="p-4 bg-slate-50 rounded-2xl outline-none focus:ring-2 focus:ring-[#18BC9C] md:col-span-2" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                                </div>
                                <textarea placeholder="Notas Hoppytime (Materiales, detalles de montaje...)" className="p-4 bg-slate-50 rounded-2xl outline-none focus:ring-2 focus:ring-[#18BC9C] h-24 resize-none" value={form.notas} onChange={e => setForm({...form, notas: e.target.value})} />
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <button type="submit" disabled={loading || (!horaSeleccionada && !usarHoraLibre)} className="py-4 rounded-2xl font-black text-white bg-[#18BC9C] hover:bg-[#15a589] shadow-lg disabled:opacity-50 transition-all uppercase tracking-widest">
                                        {loading ? 'AGENDANDO...' : 'Confirmar en GRID'}
                                    </button>
                                    <button type="button" onClick={handleBloquear} disabled={loading || (!horaSeleccionada && !usarHoraLibre)} className="py-4 rounded-2xl font-black text-white bg-slate-800 hover:bg-slate-900 shadow-lg disabled:opacity-50 transition-all uppercase tracking-widest flex items-center justify-center gap-2">
                                        <FaLock /> Bloquear Espacio
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {citasDetalle.sort((a,b) => a.horaFmt.localeCompare(b.horaFmt)).map(cita => (
                                <div key={cita.id} className={`p-5 rounded-3xl border flex flex-col shadow-sm relative ${cita.estado === 'Bloqueado' ? 'bg-slate-100 border-slate-200' : 'bg-white border-slate-100'}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <span className={`text-[10px] font-black px-3 py-1 rounded-lg uppercase ${cita.estado === 'Bloqueado' ? 'bg-slate-800 text-white' : 'bg-teal-50 text-[#18BC9C]'}`}>
                                            {cita.horaFmt} hs {cita.estado === 'Bloqueado' && '🔒'}
                                        </span>
                                        <div className="flex gap-2 z-10">
                                            {cita.estado !== 'Bloqueado' && <a href={`https://wa.me/${cita.telefonoCliente}`} target="_blank" rel="noreferrer" className="text-green-500 hover:scale-110 transition-transform"><FaWhatsapp size={20}/></a>}
                                            <button onClick={(e) => handleEliminarCita(cita.id, e)} className="text-red-300 hover:text-red-600 transition-colors"><FaTrashAlt size={18} /></button>
                                        </div>
                                    </div>
                                    <h4 className={`font-extrabold ${cita.estado === 'Bloqueado' ? 'text-slate-500' : 'text-slate-800'}`}>{cita.nombreCliente}</h4>
                                    {cita.notas && <p className="text-[11px] text-slate-500 italic mt-1">{cita.notas}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* SECCIÓN 2: 🔥 VISIBILIDAD TOTAL (TARJETAS INTERACTIVAS) */}
                <div className="bg-slate-800 p-8 rounded-[3rem] shadow-2xl overflow-hidden relative">
                    <div className="absolute top-0 right-0 p-10 opacity-10">
                        <FaListUl size={120} className="text-white" />
                    </div>
                    
                    <div className="relative z-10">
                        <h3 className="text-2xl font-black text-white mb-6 flex items-center gap-3">
                            <div className="p-3 bg-[#18BC9C] rounded-2xl">
                                <FaListUl className="text-white" />
                            </div>
                            Hoja de Ruta Global - Próximas Actividades
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {todasLasCitas.length === 0 ? (
                                <p className="text-slate-400 italic">No hay actividades programadas próximamente.</p>
                            ) : (
                                todasLasCitas.map(cita => (
                                    // 🔥 JARVIS: Tarjeta clickeable para abrir el Modal
                                    <div 
                                        key={cita.id} 
                                        onClick={() => setCitaSeleccionada(cita)}
                                        className="bg-slate-700/50 backdrop-blur-sm p-5 rounded-3xl border border-slate-600 hover:border-[#18BC9C] transition-all group shadow-sm cursor-pointer"
                                    >
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="flex flex-col">
                                                <span className="text-xl font-black text-white bg-[#18BC9C] px-3 py-1.5 rounded-xl uppercase tracking-tighter mb-2 w-fit shadow-md">{cita.fechaFmt}</span>
                                                <span className="text-lg font-bold text-white tracking-tighter">{cita.horaFmt} hs</span>
                                            </div>
                                            <button onClick={(e) => handleEliminarCita(cita.id, e)} className="text-slate-500 hover:text-red-400 transition-colors mt-1 z-10 relative">
                                                <FaTrashAlt size={16} />
                                            </button>
                                        </div>
                                        <h4 className="text-white font-extrabold text-sm mb-1 group-hover:text-[#18BC9C] transition-colors">{cita.nombreCliente}</h4>
                                        <p className="text-slate-400 text-[11px] line-clamp-2 italic">{cita.notas || 'Sin notas adicionales'}</p>
                                        
                                        <div className="mt-4 flex items-center justify-between">
                                            <div className="flex gap-2">
                                                {cita.telefonoCliente && (
                                                    <a href={`https://wa.me/${cita.telefonoCliente}`} onClick={(e) => e.stopPropagation()} target="_blank" rel="noreferrer" className="p-2 bg-green-500/10 text-green-500 rounded-lg hover:bg-green-500 hover:text-white transition-all z-10 relative">
                                                        <FaWhatsapp size={14} />
                                                    </a>
                                                )}
                                            </div>
                                            <button onClick={(e) => {
                                                e.stopPropagation();
                                                const [d, m, y] = cita.fechaFmt.split('/');
                                                setFecha(new Date(`${y}-${m}-${d}`));
                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                            }} className="text-[10px] font-bold text-slate-400 flex items-center gap-1 hover:text-white transition-colors uppercase z-10 relative">
                                                IR AL DÍA <FaChevronRight />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {/* 🔥 JARVIS: Ventana Modal para ver detalles de la Cita */}
            {citaSeleccionada && (
                <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[2rem] shadow-2xl max-w-lg w-full p-8 relative animate-in zoom-in-95 duration-200 border border-slate-200">
                        <button onClick={() => setCitaSeleccionada(null)} className="absolute top-6 right-6 text-slate-400 hover:text-slate-800 transition-colors bg-slate-100 p-2 rounded-full">
                            <FaTimes size={20} />
                        </button>
                        
                        <h2 className="text-2xl font-black text-slate-800 mb-6 border-b border-slate-100 pb-4 flex items-center gap-3 tracking-tighter">
                            <div className="p-3 bg-[#18BC9C] text-white rounded-xl">
                                <FaCalendarCheck />
                            </div>
                            Expediente de Cita
                        </h2>
                        
                        <div className="space-y-6 text-sm text-slate-700">
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Actividad / Cliente</p>
                                <p className="text-xl font-black text-slate-900">{citaSeleccionada.nombreCliente}</p>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Fecha y Hora</p>
                                    <p className="font-bold text-[#18BC9C]">
                                        {citaSeleccionada.fechaFmt} - {citaSeleccionada.horaFmt} hs
                                    </p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Origen</p>
                                    <p className="font-bold text-slate-600">{citaSeleccionada.origen || 'Interno'}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-green-50 p-4 rounded-2xl border border-green-100">
                                    <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-1">Teléfono</p>
                                    <p className="font-bold text-slate-800 flex items-center gap-2">
                                        <FaWhatsapp className="text-green-500" /> {citaSeleccionada.telefonoCliente || 'No registrado'}
                                    </p>
                                </div>
                                <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                                    <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">Email</p>
                                    <p className="font-bold text-slate-800 truncate flex items-center gap-2" title={citaSeleccionada.emailCliente}>
                                        <FaEnvelope className="text-blue-500" /> {citaSeleccionada.emailCliente || 'No registrado'}
                                    </p>
                                </div>
                            </div>

                            <div className="bg-orange-50 p-5 rounded-2xl border border-orange-100">
                                <p className="text-[10px] font-bold text-orange-800 uppercase tracking-widest mb-2 flex items-center gap-2">
                                    Notas y Detalles
                                </p>
                                <p className="whitespace-pre-wrap italic text-slate-700 font-medium leading-relaxed">
                                    {citaSeleccionada.notas || 'No se registraron notas adicionales para esta actividad.'}
                                </p>
                            </div>
                        </div>

                        <div className="mt-8 flex justify-between items-center pt-6 border-t border-slate-100">
                            <button onClick={(e) => handleEliminarCita(citaSeleccionada.id, e)} className="px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl font-bold transition-colors flex items-center gap-2 text-sm">
                                <FaTrashAlt /> Eliminar Registro
                            </button>
                            <button onClick={() => setCitaSeleccionada(null)} className="px-8 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-900 font-black transition-colors shadow-lg uppercase tracking-widest">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarioView;